// The Drafts UI's handlers, built once in App and passed down (SECOND-TOUCH-2026-10-02 §4).
// Kept out of App.jsx so the wiring is testable with a fake client and a mocked send.
import {
  saveEdit, rejectDraft, approveAndSendSms, approveEmail, markEmailSent, requestRedraft, BLOCKER_LABELS,
} from './drafts';
import { sendTimeLabel } from './quietHours';
import { setLeadState, STATE_ERROR_TEXT } from './leadState';

export function draftErrorText(e) {
  if (e && e.blockers) return 'Not sent: ' + e.blockers.map((b) => BLOCKER_LABELS[b] || b).join('; ') + '.';
  const known = {
    changed_elsewhere: 'This draft changed elsewhere -- refresh and try again. Nothing was sent.',
    reason_required: 'Give a reason to reject the draft.',
    reason_too_long: 'The reason is over 500 characters.',
    body_required: 'The draft is empty.',
    body_too_long: 'The draft is over 1600 characters.',
    no_sid: 'The worker did not confirm the send. Check the conversation before trying again.',
    state_unknown_unconfirmed: 'State unknown — confirm before sending. Nothing was sent.',
    ...STATE_ERROR_TEXT,
  };
  return known[e && e.code] || (e && e.message) || 'Something went wrong. Nothing was sent.';
}

/**
 * client      the supabase client
 * staffReply  (conversationId, body) => Promise<{sid}> -- staff.reply in production
 * setDrafts   React state setter for the drafts list
 * setBusy     id of the draft being worked on, or null
 * toast       (message) => void
 * sendEmail   ({ to, kind, subject, body, leadId?, candidateId? }) => Promise -- lib/email.js send (EMAIL-CRM-2026-10-22: from Matt
 *             through crm-mailer, logged on the timeline; no mail app). Throws with .code; recipient_suppressed = bounced.
 * leadState   (leadId) => state code, for the held-until label
 * onLeadUpdated (leadId, patch) => void -- folds a lead write (Set state) into the rendered leads, so the draft's
 *             send button reads the new state at once
 */
export function makeDraftHandlers({ client, staffReply, setDrafts, setBusy, toast, sendEmail, leadState = () => null, now,
  onLeadUpdated = () => {}, loadThread = null }) {
  const replace = (row) => setDrafts((ds) => ds.map((d) => (d.id === row.id ? { ...d, ...row } : d)));
  const run = async (draft, fn) => {
    setBusy(draft.id);
    try {
      await fn();
    } catch (e) {
      toast(draftErrorText(e));
    } finally {
      setBusy(null);
    }
  };
  const opt = now ? { now } : {};
  return {
    // A4: the one-thread transcript reader the panel uses to suggest a state from the candidate's reply.
    loadThread,
    onSave: (d, edit) => run(d, async () => {
      const { row, fails } = await saveEdit(client, d, edit);
      replace(row);
      toast(fails.length ? 'Saved. It fails the draft check, so it cannot be sent until it is fixed.' : 'Saved. It passes the draft check.');
    }),
    // { confirmUnknownState } comes from the panel's confirm dialog for a lead with no state.
    onApproveSms: (d, { confirmUnknownState = false } = {}) => run(d, async () => {
      const res = await approveAndSendSms({
        client, draft: d, sendReply: ({ conversation_id: cid, body }) => staffReply(cid, body), confirmUnknownState, ...opt,
      });
      replace(res.row);
      toast(res.sent ? 'Sent from the business line.'
        : `Approved and held: it is quiet hours where they are. Sendable from ${res.sendAt ? sendTimeLabel(res.sendAt, leadState(d.lead_id)) : 'the next window'}.`);
    }),
    // EMAIL-CRM-2026-10-22: approve FIRST (the existing write), then send; marked sent only once the mailer took it.
    // A failure leaves it approved and unsent: "Send again" or Copy. A bounce says so (the address is now No email).
    onApproveEmail: (d) => run(d, async () => {
      const res = await approveEmail({ client, draft: d, ...opt });
      replace(res.row);
      try {
        await sendEmail({ kind: 'message', ...res.email });
      } catch (e) {
        toast(e && e.code === 'recipient_suppressed'
          ? 'Not sent: the address bounced (a bounce or a complaint), so it is now No email. The draft stays approved and unsent.'
          : `Not sent: ${(e && e.message) || 'the email did not go out.'} The draft stays approved: Send again, or Copy it.`);
        return;
      }
      replace(await markEmailSent({ client, draft: { ...d, ...res.row }, ...opt }));
      toast('Sent from Matt. It is on the timeline.');
    }),
    onMarkSent: (d) => run(d, async () => {
      replace(await markEmailSent({ client, draft: d, ...opt }));
      toast('Marked sent.');
    }),
    onRedraft: (d) => run(d, async () => {
      replace(await requestRedraft({ client, draft: d, ...opt }));
      toast('Re-draft requested. The next daily run writes a fresh draft if the lead still qualifies.');
    }),
    // Set state (OVERNIGHT-AGENTS-2026-10-03 §3): writes leads.state, then folds it into the view; the toast names
    // the old and new value (the record a Mark handled leaves), and the same control sets it back.
    onSetState: (d, value) => run(d, async () => {
      const before = leadState(d.lead_id) || 'blank';
      const row = await setLeadState(client, d.lead_id, value);
      onLeadUpdated(row.id, { state: row.state });
      toast(`State set to ${row.state || 'blank'} (was ${before}).`);
    }),
    onReject: (d, reason) => run(d, async () => {
      replace(await rejectDraft(client, d, reason));
      toast('Rejected.');
    }),
  };
}
